import { Locale } from '@/i18n/config'

/**
 * 存储接口定义
 */
interface StorageInterface {
  set<T>(key: string, value: T): void
  get<T>(key: string, defaultValue?: T): T | null
  remove(key: string): void
  clear(): void
  has(key: string): boolean
}

/**
 * 检查是否在浏览器环境中
 */
const isBrowser = typeof window !== 'undefined'

/**
 * 服务端环境的空存储实现
 */
const createNoopStorage = (): StorageInterface => ({
  set: () => {},
  get: <T>(_key: string, defaultValue?: T): T | null => defaultValue ?? null,
  remove: () => {},
  clear: () => {},
  has: () => false,
})

/**
 * 创建存储工具函数
 * @param storage - Storage 对象 (localStorage 或 sessionStorage)
 * @returns 存储工具对象
 */
function createStorage(storage: Storage): StorageInterface {
  return {
    /**
     * 设置存储值
     * @param key - 存储键
     * @param value - 存储值
     */
    set<T>(key: string, value: T): void {
      if (!isBrowser) return

      try {
        storage.setItem(key, JSON.stringify(value))
      } catch (error) {
        console.error(`Failed to set storage item "${key}":`, error)
      }
    },

    /**
     * 获取存储值
     * @param key - 存储键
     * @param defaultValue - 默认值（可选）
     * @returns 存储的值或默认值
     */
    get<T>(key: string, defaultValue?: T): T | null {
      if (!isBrowser) return defaultValue ?? null

      try {
        const value = storage.getItem(key)
        if (value === null) return defaultValue ?? null

        return JSON.parse(value) as T
      } catch (error) {
        console.error(`Failed to get storage item "${key}":`, error)
        return defaultValue ?? null
      }
    },

    /**
     * 删除存储项
     * @param key - 存储键
     */
    remove(key: string): void {
      if (!isBrowser) return

      try {
        storage.removeItem(key)
      } catch (error) {
        console.error(`Failed to remove storage item "${key}":`, error)
      }
    },

    /**
     * 清空所有存储
     */
    clear(): void {
      if (!isBrowser) return

      try {
        storage.clear()
      } catch (error) {
        console.error('Failed to clear storage:', error)
      }
    },

    /**
     * 检查是否存在指定键
     * @param key - 存储键
     * @returns 是否存在
     */
    has(key: string): boolean {
      if (!isBrowser) return false

      try {
        return storage.getItem(key) !== null
      } catch (error) {
        console.error(`Failed to check storage item "${key}":`, error)
        return false
      }
    },
  }
}

/**
 * localStorage 工具
 */
export const local = isBrowser ? createStorage(localStorage) : createNoopStorage()

/**
 * sessionStorage 工具
 */
export const session = isBrowser ? createStorage(sessionStorage) : createNoopStorage()

/**
 * 从缓存中获取设备信息中的语言
 * @returns 语言代码，若未获取到则返回 undefined
 */
export function getLanguage(): Locale {
  const language = session.get<Locale>('language')
  const deviceInfo = session.get<Partial<{ language: string }>>('deviceInfo')
  return language ?? (deviceInfo?.language as Locale) ?? 'en'
}

/**
 * 存储常量
 */
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER_INFO: 'userInfo',
  THEME: 'theme',
  LANGUAGE: 'language',
} as const

/**
 * 自定义事件名称：token 变更
 */
export const TOKEN_CHANGED_EVENT = 'spg:token-changed'

/**
 * 派发 token 变更事件（仅在浏览器环境）
 */
function emitTokenChanged(token: string | null) {
  if (!isBrowser) return
  try {
    const event = new CustomEvent<string | null>(TOKEN_CHANGED_EVENT, {
      detail: token,
    })
    window.dispatchEvent(event)
  } catch (error) {
    // 忽略事件派发错误，避免影响主流程
    console.error('Failed to dispatch token changed event:', error)
  }
}

/**
 * 用户信息类型
 */
export interface UserInfo {
  avatarurl: string
  uid: string
  nickname: string
  duid: string
  accesstoken: string
  [key: string]: unknown
}

/**
 * 类型化的存储工具（基于预定义的键）
 */
export const typedStorage = {
  /**
   * 设置用户 token
   */
  setToken: (token: string) => {
    session.set(STORAGE_KEYS.TOKEN, token)
    emitTokenChanged(token)
  },

  /**
   * 获取用户 token
   */
  getToken: () => session.get<string>(STORAGE_KEYS.TOKEN),

  /**
   * 删除用户 token
   */
  removeToken: () => {
    session.remove(STORAGE_KEYS.TOKEN)
    emitTokenChanged(null)
  },

  /**
   * 设置用户信息
   */
  setUserInfo: (userInfo: UserInfo) => session.set(STORAGE_KEYS.USER_INFO, userInfo),

  /**
   * 获取用户信息
   */
  getUserInfo: <T extends UserInfo = UserInfo>() => session.get<T>(STORAGE_KEYS.USER_INFO),

  /**
   * 删除用户信息
   */
  removeUserInfo: () => session.remove(STORAGE_KEYS.USER_INFO),

  /**
   * 清除所有用户相关数据
   */
  clearUserData: () => {
    session.remove(STORAGE_KEYS.TOKEN)
    session.remove(STORAGE_KEYS.USER_INFO)
    emitTokenChanged(null)
  },

  /**
   * 清除所有缓存
   */
  clearAll: () => {
    local.clear()
    session.clear()
    emitTokenChanged(null)
  },
}
